/**
 * YouTube without pre-consent requests.
 *
 * - Embeds: markup `<div data-tune-yt="VIDEO_ID" data-tune-yt-title="...">`
 *   with a <button data-tune-yt-play> inside. Nothing loads from YouTube
 *   until the visitor clicks Play (then it autoplays) or has granted
 *   marketing consent. The iframe uses www.youtube-nocookie.com.
 * - Thumbnails (SPA galleries): youtubeThumbUrl() returns the i.ytimg.com URL
 *   only with marketing consent, otherwise a local inline placeholder, so the
 *   page makes no request to YouTube/Google before consent.
 */
import { CONSENT_EVENT } from './config'
import { readStoredConsent } from './consent-store'

const ID_RE = /^[\w-]{11}$/

export function youtubeNoCookieEmbedUrl(id: string, autoplay = false): string {
  const q = new URLSearchParams({ rel: '0', controls: '1', autoplay: autoplay ? '1' : '0', mute: '0', start: '0' })
  return `https://www.youtube-nocookie.com/embed/${encodeURIComponent(id)}?${q}`
}

export const YT_THUMB_PLACEHOLDER =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 480 360"><rect width="480" height="360" fill="#16161d"/>' +
      '<circle cx="240" cy="180" r="44" fill="#7c3aed"/><path d="M226 158v44l36-22z" fill="#fff"/></svg>'
  )

function marketingGranted(): boolean {
  return readStoredConsent()?.choices.marketing === true
}

/** Thumbnail URL that never hits YouTube before marketing consent. */
export function youtubeThumbUrl(id: string | null | undefined, quality: 'hqdefault' | 'maxresdefault' = 'hqdefault'): string {
  if (!id || !ID_RE.test(id) || !marketingGranted()) return YT_THUMB_PLACEHOLDER
  return `https://i.ytimg.com/vi/${id}/${quality}.jpg`
}

export function loadEmbed(el: HTMLElement, autoplay: boolean): HTMLIFrameElement | null {
  if (el.dataset.tuneYtLoaded === '1') return el.querySelector('iframe')
  const id = el.dataset.tuneYt || ''
  if (!ID_RE.test(id)) return null
  const f = document.createElement('iframe')
  f.src = youtubeNoCookieEmbedUrl(id, autoplay)
  f.title = el.dataset.tuneYtTitle || 'YouTube video'
  f.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture')
  f.setAttribute('allowfullscreen', '')
  f.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin')
  f.setAttribute('frameborder', '0')
  f.style.cssText = 'position:absolute;left:0;top:0;width:100%;height:100%;border:0'
  const slot = (el.querySelector('[data-tune-yt-slot]') as HTMLElement) || el
  slot.querySelector('[data-tune-yt-play]')?.remove()
  slot.appendChild(f)
  el.dataset.tuneYtLoaded = '1'
  if (autoplay) f.focus?.()
  return f
}

/** Wire every [data-tune-yt] placeholder in root. Idempotent. */
export function initClickToLoadEmbeds(root: Document | HTMLElement = document): void {
  const all = () => [...root.querySelectorAll<HTMLElement>('[data-tune-yt]')]
  for (const el of all()) {
    if (el.dataset.tuneYtWired === '1') continue
    el.dataset.tuneYtWired = '1'
    el.querySelector('[data-tune-yt-play]')?.addEventListener('click', (e) => {
      e.preventDefault()
      e.stopPropagation()
      loadEmbed(el, true)
    })
  }
  const maybeAll = () => {
    if (marketingGranted()) all().forEach((el) => loadEmbed(el, false))
  }
  maybeAll()
  if (typeof window !== 'undefined' && !(window as any).__tuneYtListening) {
    ;(window as any).__tuneYtListening = true
    window.addEventListener(CONSENT_EVENT, maybeAll)
  }
}
